import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar, ChevronDown, ChevronRight, Filter, Info, RotateCcw } from 'lucide-react';
import { useIsDesktop, useIsLargeScreen } from '../hooks/useIsDesktop';
import { cleanRepeatedText, cn, formatSlashDate } from '../lib/utils';

interface Document {
  docGuid: string;
  title: string;
  description?: string;
  expirationDate?: string;
  summary?: string;
  /** Completion flag from API (boolean or legacy string). Same semantics as Document page: done vs in progress. */
  status?: string | boolean | number;
  operationId?: string;
}

/** Matches Document page: `status === true` means Done; list API may send booleans or strings like signed/pending. */
function isDocumentListDone(status: unknown): boolean {
  if (status === true || status === 1) return true;
  if (status === false || status === 0) return false;
  if (typeof status === 'string') {
    const s = status.trim().toLowerCase();
    return s === 'true' || s === 'signed' || s === 'done';
  }
  return false;
}

type SignStatusFilterMode = 'all' | 'signed' | 'pending';

interface DocumentListAppliedFilters {
  signStatusMode: SignStatusFilterMode;
  createdFrom: string;
  expiresBefore: string;
  operationId: string;
}

const defaultAppliedFilters: DocumentListAppliedFilters = {
  signStatusMode: 'all',
  createdFrom: '',
  expiresBefore: '',
  operationId: '',
};

function normalizeDocumentsResponse(data: unknown): Document[] {
  if (Array.isArray(data)) return data as Document[];
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const list = o.documents ?? o.data;
    if (Array.isArray(list)) return list as Document[];
  }
  return [];
}

function mergeDocumentsByGuid(lists: Document[][]): Document[] {
  const map = new Map<string, Document>();
  for (const list of lists) {
    for (const doc of list) {
      if (doc?.docGuid && !map.has(doc.docGuid)) {
        map.set(doc.docGuid, doc);
      }
    }
  }
  return Array.from(map.values());
}

function buildDocumentListQueryParams(opts: {
  signStatus: boolean;
  createdFrom: string;
  expiresBefore: string;
  operationId: string;
}): string {
  const params = new URLSearchParams();
  params.set('SignStatus', String(opts.signStatus));
  if (opts.createdFrom) {
    params.set('CreatedAt', opts.createdFrom);
  }
  if (opts.expiresBefore) {
    params.set('ExpirationDate', opts.expiresBefore);
  }
  const op = opts.operationId.trim();
  if (op) {
    params.set('OperationId', op);
  }
  return params.toString();
}

function countActiveFilters(filters: DocumentListAppliedFilters): number {
  let count = 0;
  if (filters.signStatusMode !== defaultAppliedFilters.signStatusMode) count += 1;
  if (filters.createdFrom) count += 1;
  if (filters.expiresBefore) count += 1;
  if (filters.operationId.trim()) count += 1;
  return count;
}

const DocumentList: React.FC = () => {
  const { t } = useLanguage();
  const { token, isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isLargeScreen = useIsLargeScreen();
  const useCompactList = !isLargeScreen;

  const [signStatusMode, setSignStatusMode] = useState<SignStatusFilterMode>(
    defaultAppliedFilters.signStatusMode,
  );
  const [createdFrom, setCreatedFrom] = useState('');
  const [expiresBefore, setExpiresBefore] = useState('');
  const [operationIdFilter, setOperationIdFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<DocumentListAppliedFilters>(defaultAppliedFilters);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const activeFilterCount = countActiveFilters(appliedFilters);
  const filtersVisible = isDesktop || filtersExpanded;

  const fetchDocuments = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setError(t('pleaseLoginToView'));
      return;
    }

    setIsLoading(true);
    setError(null);

    const baseUrl = `${process.env.REACT_APP_SIGNER_API_URL}/api/Document/GetDocuments`;
    const { signStatusMode: mode, createdFrom: created, expiresBefore: expires, operationId } = appliedFilters;
    const common = {
      createdFrom: created,
      expiresBefore: expires,
      operationId,
    };

    try {
      let documentsList: Document[] = [];

      if (mode === 'all') {
        const qFalse = buildDocumentListQueryParams({ signStatus: false, ...common });
        const qTrue = buildDocumentListQueryParams({ signStatus: true, ...common });
        const [resFalse, resTrue] = await Promise.all([
          fetch(`${baseUrl}?${qFalse}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${baseUrl}?${qTrue}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        if (!resFalse.ok || !resTrue.ok) {
          const bad = !resFalse.ok ? resFalse : resTrue;
          const errorData = await bad.json().catch(() => ({ message: t('failedToLoadDocuments') }));
          throw new Error(errorData.message || t('failedToLoadDocuments'));
        }

        const [dataFalse, dataTrue] = await Promise.all([resFalse.json(), resTrue.json()]);
        documentsList = mergeDocumentsByGuid([
          normalizeDocumentsResponse(dataFalse),
          normalizeDocumentsResponse(dataTrue),
        ]);
      } else {
        const query = buildDocumentListQueryParams({
          signStatus: mode === 'signed',
          ...common,
        });
        const response = await fetch(`${baseUrl}?${query}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: t('failedToLoadDocuments') }));
          throw new Error(errorData.message || t('failedToLoadDocuments'));
        }

        const data = await response.json();
        documentsList = normalizeDocumentsResponse(data);
      }

      setDocuments(documentsList);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : t('failedToLoadDocuments'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, token, t, appliedFilters]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleViewDocument = (docGuid: string) => {
    navigate(`/document?did=${docGuid}`);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      signStatusMode,
      createdFrom,
      expiresBefore,
      operationId: operationIdFilter,
    });
    if (!isDesktop) {
      setFiltersExpanded(false);
    }
  };

  const handleResetFilters = () => {
    setSignStatusMode(defaultAppliedFilters.signStatusMode);
    setCreatedFrom('');
    setExpiresBefore('');
    setOperationIdFilter('');
    setAppliedFilters({ ...defaultAppliedFilters });
    if (!isDesktop) {
      setFiltersExpanded(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Alert variant="destructive">{t('pleaseLoginToView')}</Alert>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0">
        <Card className="mb-2 rounded-none md:mb-4">
          <CardContent className="p-4">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 text-left md:pointer-events-none"
              onClick={() => setFiltersExpanded((open) => !open)}
              aria-expanded={filtersVisible}
              aria-controls="document-list-filters"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('documentFilters')}</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary">{activeFilterCount}</Badge>
                )}
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform md:hidden',
                  filtersExpanded && 'rotate-180',
                )}
                aria-hidden
              />
              <span className="sr-only md:hidden">
                {filtersExpanded ? t('hideFilters') : t('showFilters')}
              </span>
            </button>
            <div
              id="document-list-filters"
              className={cn('space-y-4', filtersVisible ? 'mt-4 block' : 'hidden', 'md:mt-4 md:block')}
            >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="doc-filter-status">{t('signingStatusFilter')}</Label>
                <Select
                  value={signStatusMode}
                  onValueChange={(v) => setSignStatusMode(v as SignStatusFilterMode)}
                >
                  <SelectTrigger id="doc-filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('signStatusAll')}</SelectItem>
                    <SelectItem value="pending">{t('signStatusPending')}</SelectItem>
                    <SelectItem value="signed">{t('signStatusSigned')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc-filter-created">{t('createdFromFilter')}</Label>
                <Input
                  id="doc-filter-created"
                  type="date"
                  value={createdFrom}
                  onChange={(e) => setCreatedFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc-filter-expires">{t('expiresBeforeFilter')}</Label>
                <Input
                  id="doc-filter-expires"
                  type="date"
                  value={expiresBefore}
                  onChange={(e) => setExpiresBefore(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc-filter-op">{t('operationIdFilter')}</Label>
                <Input
                  id="doc-filter-op"
                  type="text"
                  placeholder={t('operationIdFilterPlaceholder')}
                  value={operationIdFilter}
                  onChange={(e) => setOperationIdFilter(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={handleApplyFilters} disabled={isLoading}>
                {t('applyFilters')}
              </Button>
              <Button type="button" variant="outline" onClick={handleResetFilters} disabled={isLoading}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('resetFilters')}
              </Button>
            </div>
            </div>
          </CardContent>
        </Card>

        {error && <Alert variant="destructive" className="mb-2">{error}</Alert>}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {isLoading ? (
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : documents.length === 0 ? (
          <Card className="rounded-none"><CardContent className="p-6"><h3 className="text-lg font-medium">{t('noDocumentsFound')}</h3><p className="text-sm text-muted-foreground">{t('noDocumentsYet')}</p></CardContent></Card>
        ) : useCompactList ? (
          <div className="w-full flex-1 space-y-2 overflow-y-auto pb-2">
            {documents.map((doc) => {
              const done = isDocumentListDone(doc.status);
              return (
              <Card key={doc.docGuid} className="cursor-pointer rounded-none" onClick={() => handleViewDocument(doc.docGuid)}>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold" title={cleanRepeatedText(doc.title) || t('untitledDocument')}>
                        {cleanRepeatedText(doc.title) || t('untitledDocument')}
                      </h3>
                      <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><Calendar className="h-4 w-4" /> {formatSlashDate(doc.expirationDate)}</p>
                      {doc.operationId && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Info className="h-3 w-3" /> {t('operation')}: {doc.operationId}</p>}
                    </div>
                    <ChevronRight className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant={done ? 'success' : 'warning'}>
                      {done ? t('documentStatusDone') : t('documentStatusInProgress')}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 overflow-auto rounded-none border bg-background">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">{t('title')}</th>
                  <th className="px-4 py-3 font-semibold">{t('description')}</th>
                  <th className="px-4 py-3 font-semibold">{t('operationId')}</th>
                  <th className="px-4 py-3 font-semibold">{t('expirationDate')}</th>
                  <th className="px-4 py-3 font-semibold">{t('status')}</th>
                  <th className="w-10 px-4 py-3" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const done = isDocumentListDone(doc.status);
                  const docTitle = cleanRepeatedText(doc.title) || t('untitledDocument');
                  return (
                  <tr
                    key={doc.docGuid}
                    className="cursor-pointer border-t transition-colors hover:bg-muted/50"
                    onClick={() => handleViewDocument(doc.docGuid)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewDocument(doc.docGuid);
                      }
                    }}
                    tabIndex={0}
                    aria-label={`${t('view')} ${docTitle}`}
                  >
                    <td className="px-4 py-3"><span className="block max-w-[300px] truncate">{docTitle}</span></td>
                    <td className="px-4 py-3">
                      <span className="block max-w-[320px] truncate text-muted-foreground" title={cleanRepeatedText(doc.description) || '-'}>
                        {cleanRepeatedText(doc.description) || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="block max-w-[220px] truncate text-muted-foreground" title={doc.operationId || '-'}>
                        {doc.operationId || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatSlashDate(doc.expirationDate)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={done ? 'success' : 'warning'}>
                        {done ? t('documentStatusDone') : t('documentStatusInProgress')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="inline h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentList;
